/**
 * Backend Analytics Provider
 * Sends system events to backend Elasticsearch.
 * Only handles events it cares about — other events return null from transform().
 */

import { BaseAnalyticsProvider } from './base.provider'
import { AnalyticsEvents } from '../types/events'
import { backendAnalyticsService } from '../services/backend-analytics.service'
import type { AnalyticsEventProperties } from '../types/properties'

// Backend event name mapping (ONLY events this provider handles)
const BACKEND_EVENT_MAP: Record<string, string> = {
  [AnalyticsEvents.USER_AUTO_LOGOUT]: 'client-portal-user-auto-logout',
  [AnalyticsEvents.COMMUNITIES_USER_AUTO_LOGOUT]: 'communities-user-auto-logout',
  [AnalyticsEvents.GENERATE_TOKEN_FAILED]: 'generate-token-failed',
  [AnalyticsEvents.GENERATE_TOKEN_SUCCESS]: 'generate-token-success',
  [AnalyticsEvents.COMMUNITIES_GROUP_ACCESS]: 'communities-group-access-event',
  [AnalyticsEvents.NO_COMMUNITIES_FOUND]: 'no-communities-found',
  [AnalyticsEvents.COURSE_ACCESS_REFRESH]: 'course-access-refresh',
  [AnalyticsEvents.PUSH_NOTIFICATION_INITIALIZED]: 'client-portal-initialize-push-event',
  [AnalyticsEvents.FORCE_UPDATE_EVENT]: 'client-portal-force-app-update-event',
  // Dedicated resolver-error event ONLY. The generic ERROR_OCCURRED is intentionally NOT mapped:
  // its window error/unhandledrejection payloads carry raw, unredacted strings that must not reach
  // the (unauthenticated) backend ES sink.
  [AnalyticsEvents.ANALYTICS_CONTEXT_RESOLVER_ERROR]: 'analytics-context-resolver-error',
}

interface BackendPayload {
  eventName: string
  locationId: string
  contactId: string
  groupId?: string
  eventData: Record<string, unknown>
}

export class BackendAnalyticsProvider extends BaseAnalyticsProvider<BackendPayload> {
  readonly name = 'BackendAnalytics'
  private debug = false
  private currentUser: { userId?: string; locationId?: string; contactId?: string } = {}

  constructor(debug = false) {
    super()
    this.debug = debug
  }

  transform(event: string, properties: AnalyticsEventProperties): BackendPayload | null {
    const backendEventName = BACKEND_EVENT_MAP[event]
    if (!backendEventName) return null

    return {
      eventName: backendEventName,
      locationId: properties.locationId || this.currentUser.locationId || 'NA',
      contactId: properties.contactId || this.currentUser.contactId || 'NA',
      groupId: properties.groupId,
      eventData: {
        ...properties,
        userId: event === AnalyticsEvents.ANALYTICS_CONTEXT_RESOLVER_ERROR ? undefined : this.currentUser.userId,
        platform: properties.platform || 'web',
      },
    }
  }

  send(payload: BackendPayload): void {
    try {
      backendAnalyticsService.trackEvent(payload)
      if (this.debug) console.log('[BackendAnalytics] Event tracked:', payload.eventName, payload)
    } catch (error) {
      console.error('[BackendAnalytics] Error:', error)
    }
  }

  identify(contactId: string, traits?: Record<string, unknown>): void {
    this.currentUser = {
      contactId,
      userId: (traits?.userId as string) || undefined,
      locationId: traits?.locationId as string | undefined,
    }
    if (this.debug) console.log('[BackendAnalytics] User identified:', this.currentUser)
  }
}
